import React, { useState } from 'react';
import { Button, ButtonGroup, Drawer, SubmitButton } from '@common/components';
import { useMutation, useQuery } from '@common/hooks';
import {
  ArchiveWorkforceGroup,
  UpdateWorkforceGroup,
} from '@scheduling/graphql/mutations/workforce-group';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  convertToType,
  resetUtilizationOnUnrestricted,
} from '@scheduling/services/workforce-group-utils';
import { WorkforceGroupById } from '@scheduling/graphql';
import {
  FteAttainmentMethod,
  FteAttainmentPeriod,
  WorkforceGroupByIdQueryCgResponse,
  WorkforceGroupUtilizationRule,
} from '@common/graphql/schema';
import {
  WorkforceGroupSaveModal,
  WorkforceGroupDrawerBaseForm,
  activeWorkersInfoModal,
  confirmArchiving,
} from '@scheduling/components';

export type UpdateWorkforceGroupInputDataType = {
  name: string;
  gateOpenShiftsVisibility: boolean;
  openShiftsVisibilityDelayHoursNumber: string;
  openShiftsVisibilityDelayHoursType: string;
  gateEarliestAdvanceTime: boolean;
  earliestAdvanceTimeNumber: string;
  earliestAdvanceTimeType: string;
  weeklyUtilization: [number, number];
  consecutiveHoursLimit: number;
  consecutiveHoursBridgeTolerance: number;
  consecutiveHoursResetPeriod: number;
  utilizationRule: WorkforceGroupUtilizationRule;
  fteAttainmentEnabled: boolean;
  fteAttainmentMethod?: FteAttainmentMethod;
  fteAttainmentPeriod?: FteAttainmentPeriod;
  fteFixedHours?: number;
};

const inputDataDefault: UpdateWorkforceGroupInputDataType = {
  name: '',
  gateOpenShiftsVisibility: false,
  weeklyUtilization: [0, 168],
  gateEarliestAdvanceTime: false,
  earliestAdvanceTimeNumber: '0',
  earliestAdvanceTimeType: 'days',
  openShiftsVisibilityDelayHoursNumber: '0',
  openShiftsVisibilityDelayHoursType: 'days',
  consecutiveHoursLimit: 0,
  consecutiveHoursBridgeTolerance: 0,
  consecutiveHoursResetPeriod: 0,
  utilizationRule: 'UNRESTRICTED',
  fteAttainmentEnabled: false,
};

type SelectType = 'days' | 'hours';

export function WorkforceGroupUpdateDrawer({
  open,
  onClose,
  workforceGroupId,
}: {
  open: boolean;
  onClose: () => void;
  workforceGroupId?: string;
}) {
  const [inputData, setInputData] = useState(inputDataDefault);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [updateWorkforceGroup, { loading }] = useMutation(
    UpdateWorkforceGroup,
    {
      onCompleted: (data) => {
        if (
          data?.updateWorkforceGroup?.errors &&
          data?.updateWorkforceGroup.errors.length > 0
        ) {
          createMessage(
            'error',
            `Failed to update the group. ${data?.updateWorkforceGroup?.errors?.[0]?.fullErrors[0]}.`
          );
          return;
        }
        createMessage('success', 'Group successfully saved');
        setInputData(inputDataDefault);
        onClose();
      },
      onError: () => {
        createMessage(
          'error',
          "Group couldn't be updated at this time. Try again later or contact support."
        );
      },
      refetchQueries: [
        'WorkforceGroupsQuery',
        'WorkforceGroupQuery',
        'WorkforceGroupById',
      ],
    }
  );

  const updateInputData = (
    response: WorkforceGroupByIdQueryCgResponse | undefined
  ) => {
    if (!response?.workforceGroup) {
      return;
    }
    const defaultConfig = response.workforceGroup.defaultConfig;
    if (!defaultConfig) {
      return;
    }
    const {
      gateOpenShiftsVisibility,
      openShiftsVisibilityDelayHours,
      openShiftsVisibilityDelayHoursFormat,
      gateEarliestAdvanceTime,
      earliestAdvanceTime,
      earliestAdvanceTimeFormat,
    } = defaultConfig;

    const {
      name,
      weeklyMaximumHours,
      weeklyMinimumHours,
      consecutiveHoursLimit,
      consecutiveHoursBridgeTolerance,
      consecutiveHoursResetPeriod,
      utilizationRule,
      fteAttainmentEnabled,
      fteAttainmentMethod,
      fteAttainmentPeriod,
      fteFixedHours,
    } = response.workforceGroup;

    setInputData({
      name,
      gateOpenShiftsVisibility:
        gateOpenShiftsVisibility ?? inputDataDefault.gateOpenShiftsVisibility,
      weeklyUtilization: [weeklyMinimumHours, weeklyMaximumHours],
      gateEarliestAdvanceTime:
        gateEarliestAdvanceTime ?? inputDataDefault.gateEarliestAdvanceTime,
      consecutiveHoursLimit:
        consecutiveHoursLimit ?? inputDataDefault.consecutiveHoursLimit,
      consecutiveHoursBridgeTolerance:
        consecutiveHoursBridgeTolerance ??
        inputDataDefault.consecutiveHoursBridgeTolerance,
      consecutiveHoursResetPeriod:
        consecutiveHoursResetPeriod ??
        inputDataDefault.consecutiveHoursResetPeriod,
      utilizationRule: utilizationRule ?? inputDataDefault.utilizationRule,
      fteAttainmentEnabled:
        fteAttainmentEnabled ?? inputDataDefault.fteAttainmentEnabled,
      fteAttainmentMethod: fteAttainmentMethod ?? undefined,
      fteAttainmentPeriod: fteAttainmentPeriod ?? undefined,
      fteFixedHours: fteFixedHours ?? undefined,
      earliestAdvanceTimeNumber: convertToType(
        earliestAdvanceTime?.toString() || '0',
        'hours',
        (earliestAdvanceTimeFormat?.toString() ||
          inputDataDefault.earliestAdvanceTimeType) as SelectType
      ).toString(),
      earliestAdvanceTimeType:
        earliestAdvanceTimeFormat ?? inputDataDefault.earliestAdvanceTimeType,
      openShiftsVisibilityDelayHoursNumber: convertToType(
        openShiftsVisibilityDelayHours?.toString() || '0',
        'hours',
        (openShiftsVisibilityDelayHoursFormat?.toString() ||
          inputDataDefault.openShiftsVisibilityDelayHoursType) as SelectType
      ).toString(),
      openShiftsVisibilityDelayHoursType:
        openShiftsVisibilityDelayHoursFormat ??
        inputDataDefault.openShiftsVisibilityDelayHoursType,
    });
  };

  const { data: initialData } = useQuery(WorkforceGroupById, {
    variables: {
      workforceGroupId: workforceGroupId || '',
    },
    onCompleted: updateInputData,
  });

  const [archiveWorkforceGroup] = useMutation(ArchiveWorkforceGroup, {
    refetchQueries: ['WorkforceGroupQuery', 'WorkforceGroupById'],
    onCompleted: onDrawerClose,
  });

  const onUpdateInputData = (
    key: string,
    value: string | boolean | number | [number, number]
  ) => {
    setInputData((prev) =>
      resetUtilizationOnUnrestricted(prev, key, value, inputDataDefault)
    );
  };

  const onSave = (override?: boolean) => {
    const data = {
      workforceGroupId: workforceGroupId || '',
      overrideAllSettings: override || false,
      name: inputData.name,
      weeklyMaximumHours: inputData.weeklyUtilization[1],
      weeklyMinimumHours: inputData.weeklyUtilization[0],
      consecutiveHoursLimit: inputData.consecutiveHoursLimit,
      consecutiveHoursBridgeTolerance:
        inputData.consecutiveHoursBridgeTolerance,
      consecutiveHoursResetPeriod: inputData.consecutiveHoursResetPeriod,
      utilizationRule: inputData.utilizationRule,
      gateEarliestAdvanceTime: inputData.gateEarliestAdvanceTime,
      earliestAdvanceTime: convertToType(
        inputData.earliestAdvanceTimeNumber,
        inputData.earliestAdvanceTimeType as 'days' | 'hours',
        'hours'
      ),
      earliestAdvanceTimeFormat: inputData.earliestAdvanceTimeType,
      gateOpenShiftsVisibility: inputData.gateOpenShiftsVisibility,
      openShiftsVisibilityDelayHours: convertToType(
        inputData.openShiftsVisibilityDelayHoursNumber,
        inputData.openShiftsVisibilityDelayHoursType as 'days' | 'hours',
        'hours'
      ),
      openShiftsVisibilityDelayHoursFormat:
        inputData.openShiftsVisibilityDelayHoursType,
      fteAttainmentEnabled: inputData.fteAttainmentEnabled,
      fteAttainmentMethod: inputData.fteAttainmentMethod,
      fteAttainmentPeriod: inputData.fteAttainmentPeriod,
      fteFixedHours: inputData.fteFixedHours,
    };

    updateWorkforceGroup({
      variables: {
        input: data,
      },
    });
  };

  const isGroupActive = initialData?.workforceGroup?.active || false;

  function onDrawerClose() {
    updateInputData(initialData);
    onClose();
  }

  function archiveGroup() {
    if (!workforceGroupId) return;
    if (!isGroupActive) return;

    const hasActiveWorkers =
      (initialData?.workforceGroup?.activeWorkersCount || 0) > 0;
    if (hasActiveWorkers) {
      activeWorkersInfoModal(workforceGroupId);
    } else {
      confirmArchiving(() => {
        archiveWorkforceGroup({
          variables: {
            input: {
              workforceGroupId,
            },
          },
        });
      });
    }
  }

  const footer = (
    <ButtonGroup>
      <Button onClick={onDrawerClose}>Cancel</Button>
      <Button
        danger
        disabled={!workforceGroupId || !isGroupActive}
        onClick={archiveGroup}
      >
        Archive
      </Button>
      <SubmitButton
        onClick={() => setConfirmModalVisible(true)}
        loading={loading}
      >
        Save
      </SubmitButton>
    </ButtonGroup>
  );

  return (
    <Drawer
      open={open}
      className="admin-wf-group-update-drawer"
      title="Edit Group"
      footer={footer}
      onClose={onDrawerClose}
    >
      <WorkforceGroupSaveModal
        modalVisible={confirmModalVisible}
        onConfirmModal={onSave}
        loading={loading}
        onCancelModal={() => setConfirmModalVisible(false)}
      />
      <WorkforceGroupDrawerBaseForm
        onUpdateInputData={onUpdateInputData}
        inputData={inputData}
      />
    </Drawer>
  );
}
